import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { listTasks, retryTask } from '@/api/tasks';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { FAILURE_STATUSES } from '@/types/api';

export default function RecentFailuresPanel() {
  const { t } = useTranslation('dashboard');
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['tasks', 'failures'],
    queryFn: () =>
      listTasks({ status: FAILURE_STATUSES, limit: 10, order: 'started_desc' }),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const items = q.data?.items ?? [];

  const retryMutation = useMutation({
    mutationFn: retryTask,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['statuses'] });
    },
  });

  return (
    <div className="flex flex-col rounded-md border bg-card">
      <div className="border-b px-3 py-2 text-xs font-medium">
        {t('failures.title', { n: items.length })}
      </div>
      {items.length === 0 ? (
        <div className="p-3 text-xs text-muted-foreground">{t('failures.empty')}</div>
      ) : (
        <ul className="divide-y text-xs">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-2 px-3 py-2">
              <Link to={`/tasks/${item.id}`} className="truncate hover:underline">
                {'📞'} {item.num_a ?? '—'}
              </Link>
              <StatusBadge status={item.status} />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="ml-auto h-6 px-2 text-xs"
                disabled={retryMutation.isPending && retryMutation.variables === item.id}
                onClick={() => retryMutation.mutate(item.id)}
              >
                {t('failures.retry')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
